import { httpClient } from '@/core/api/httpClient';
import { env } from '@/core/config/env';
import { API_V1 } from '@/core/api/endpoints';
import type {
  MyInvitation,
  AcceptInvitationRequest,
  AcceptInvitationResponse,
  InvitationPublicInfo,
} from '../types';
import { invitationsMock, invitationPublicInfoMock } from './invitations.mock';

const USE_MOCK = env.VITE_USE_MOCK === 'true';

export const invitationsService = {
  getMyInvitations: async (signal?: AbortSignal): Promise<MyInvitation[]> => {
    if (USE_MOCK) return invitationsMock;
    const url = `${API_V1}/me/invitations`;
    const { data } = await (signal
      ? httpClient.get<MyInvitation[]>(url, { signal })
      : httpClient.get<MyInvitation[]>(url));
    return data;
  },

  /**
   * Resolves public invitation details by token (email-link flow).
   * The endpoint requires `X-Tenant-Id`; the active organization in session is
   * injected automatically by httpClient.
   */
  getInvitationByToken: async (
    token: string,
    signal?: AbortSignal,
  ): Promise<InvitationPublicInfo> => {
    if (USE_MOCK) return invitationPublicInfoMock;
    const url = `${API_V1}/invitations/${token}`;
    const { data } = await (signal
      ? httpClient.get<InvitationPublicInfo>(url, { signal })
      : httpClient.get<InvitationPublicInfo>(url));
    return data;
  },

  acceptInvitation: async (payload: AcceptInvitationRequest): Promise<AcceptInvitationResponse> => {
    if (USE_MOCK) return { memberId: 'mock-member-id' };
    const { data } = await httpClient.post<AcceptInvitationRequest, AcceptInvitationResponse>(
      `${API_V1}/invitations/accept`,
      payload,
    );
    return data;
  },

  /**
   * Accepts a pending invitation by ID (in-app flow).
   * POST /api/me/invitations/{invitationId}/accept
   */
  acceptInvitationById: async (invitationId: string): Promise<void> => {
    if (USE_MOCK) return;
    await httpClient.post<Record<string, never>, void>(
      `${API_V1}/me/invitations/${invitationId}/accept`,
      {},
    );
  },

  rejectInvitation: async (invitationId: string): Promise<void> => {
    if (USE_MOCK) return;
    await httpClient.post<Record<string, never>, void>(
      `${API_V1}/me/invitations/${invitationId}/reject`,
      {},
    );
  },

  /**
   * Rejects an invitation by token (email-link flow).
   * POST /api/invitations/{token}/reject
   */
  rejectInvitationByToken: async (token: string): Promise<void> => {
    if (USE_MOCK) return;
    await httpClient.post<Record<string, never>, void>(`${API_V1}/invitations/${token}/reject`, {});
  },
};
